import { HISTORY_ACTION_MODE } from '@candulabs/craft';
import { Button, Label, Modal, Stack, TextInput, Tooltip } from 'canoe';
import { nanoid } from 'nanoid';
import React, { useMemo, useRef, useState } from 'react';

import { useNodeConditional } from 'craft/containers/Conditionals';
import { useEditor } from 'craft/hooks';
import { useFlow } from 'craft/state';
import { createConditionalFlowStep } from 'craft/state/flows/createConditionalFlowStep';
import { ThenType } from 'craft/types';
import { SegmentDefinition } from 'dashboard/pages/Segment/components';
import { DefinitionFormRaw } from 'dashboard/pages/Segment/components/DefinitionForm';

interface Props {
  nodeId: string;
  isEditingIndex: number | null;
  onClose: () => void;
}

const emptySegment = {
  groups: [],
  object: 'Any',
};

export const OptionModalConditional = ({ nodeId, isEditingIndex, onClose }: Props) => {
  const { conditional, setConditional } = useNodeConditional(nodeId);
  const { stepIds, stepNames, onAddStep } = useFlow(nodeId);
  const { actions } = useEditor();

  const originalViewName = useRef<string>(isEditingIndex !== null ? stepNames[isEditingIndex] : '');

  const [viewName, setViewName] = useState(originalViewName.current);

  const removeStateProvider = () => {
    actions.produceState((state) => {
      // eslint-disable-next-line no-param-reassign
      state.nodes[nodeId].data.custom.states = null;
    }, HISTORY_ACTION_MODE.MERGE);
  };

  const segmentId = useMemo(() => conditional?.if.id || `ds-${nanoid(6)}`, [conditional]);
  return (
    <Modal onClose={onClose} closeOnClickOutside modalWidth="large">
      <Modal.Header
        css={{ maxWidth: '100%' }}
        title={`Show ${originalViewName.current || 'New View'} When...`}
      />
      <Modal.Body>
        <Stack space="normal">
          <div>
            <Label necessity="required" spaceAfter="xxs">
              View Name
            </Label>
            <TextInput
              placeholder="Name your View"
              value={viewName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setViewName(e.target.value)}
              state={isEditingIndex !== null && !viewName ? 'danger' : 'default'}
            />
          </div>
          <div>
            <Label necessity="required">Conditions</Label>
            <DefinitionFormRaw
              definition={
                conditional?.if?.definition ?? ((emptySegment as any) as SegmentDefinition)
              }
              segmentId={segmentId}
              onUpdate={(definition) => {
                const conditionalDefinition = {
                  if: { id: segmentId, definition },
                  then: [{ type: ThenType.ChecklistItem }],
                };

                if (isEditingIndex !== null) {
                  setConditional(conditionalDefinition);
                  removeStateProvider();

                  if (viewName !== originalViewName.current) {
                    actions.setProp(stepIds[isEditingIndex], (props: { name: string }) => {
                      // eslint-disable-next-line no-param-reassign
                      props.name = viewName;
                    });
                  }
                } else {
                  onAddStep(createConditionalFlowStep(viewName, conditionalDefinition));
                }

                onClose();
              }}
            >
              <div
                css={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '4px',
                }}
              >
                <Button variant="neutral" onClick={onClose}>
                  Cancel
                </Button>
                <Tooltip
                  content="View must have a name"
                  disabled={!!viewName || isEditingIndex !== null}
                >
                  <Button variant="secondary" type="submit" disabled={!viewName}>
                    {isEditingIndex === null ? 'Save' : 'Update'}
                  </Button>
                </Tooltip>
              </div>
            </DefinitionFormRaw>
          </div>
        </Stack>
      </Modal.Body>
    </Modal>
  );
};
